import { LoopContext, Variable } from '../types'

export interface LoopFlowVisualizerOptions {
  canvas: HTMLCanvasElement
  backgroundColor?: string
  primaryColor?: string
  secondaryColor?: string
  errorColor?: string
}

interface LoopNode {
  id: number  // ノードの一意なID
  type: 'for' | 'while' | 'do-while'
  level: number
  x: number
  y: number
  width: number
  height: number
  iteration: number
  condition: string
  evaluatedCondition: string
  conditionResult: boolean
  isActive: boolean
  parentIteration?: number  // 親ループのイテレーション番号（ネストしたループ用）
  variables?: Variable[]  // イテレーション時の変数値
  variablesAfter?: Variable[]  // イテレーション終了時の変数値
  // アニメーション用プロパティ
  targetX?: number
  targetY?: number
  opacity: number
  scale: number
  glowIntensity: number
}

export class LoopFlowVisualizer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private options: Required<LoopFlowVisualizerOptions>
  private loopNodes: LoopNode[] = []
  private animationFrame: number | null = null
  private scale: number = 1
  private isAnimating: boolean = false
  private animationStartTime: number = 0
  private flowParticles: FlowParticle[] = []
  private canvasWidth: number = 800
  private canvasHeight: number = 600
  private executionHistory: LoopNode[] = []  // 実行順序で履歴を保持
  private lastLoopStack: string = ''  // 前回のループスタックの状態
  private nodeIdCounter: number = 0  // ノードの一意なIDを生成するカウンター
  
  constructor(options: LoopFlowVisualizerOptions) {
    this.canvas = options.canvas
    const ctx = this.canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas context not available')
    }
    this.ctx = ctx
    
    this.options = {
      canvas: options.canvas,
      backgroundColor: options.backgroundColor || '#f9fafb',
      primaryColor: options.primaryColor || '#2563eb',
      secondaryColor: options.secondaryColor || '#10b981',
      errorColor: options.errorColor || '#ef4444',
    }
    
    this.setupCanvas()
    this.startAnimation()
  }
  
  /**
   * Canvasの初期設定
   */
  private setupCanvas(): void {
    // 高DPIディスプレイ対応
    const dpr = window.devicePixelRatio || 1
    
    // 初期キャンバスサイズ
    this.updateCanvasSize(this.canvasWidth, this.canvasHeight)
    
    // スケール調整
    this.scale = 1.2
    
    // 必要に応じてリサイズ処理を追加
  }
  
  /**
   * 再描画
   */
  render(): void {
    this.draw()
  }
  
  /**
   * キャンバスサイズを更新
   */
  private updateCanvasSize(width: number, height: number): void {
    const dpr = window.devicePixelRatio || 1
    
    // キャンバスのCSSサイズを設定
    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
    
    // 実際のキャンバスサイズを設定（高DPI対応）
    this.canvas.width = width * dpr
    this.canvas.height = height * dpr
    
    // コンテキストをスケール
    this.ctx.scale(dpr, dpr)
    
    // サイズを記録
    this.canvasWidth = width
    this.canvasHeight = height
  }
  
  /**
   * ループスタックを更新
   */
  updateLoopStack(loopStack: LoopContext[]): void {
    // 履歴を更新
    this.updateExecutionHistory(loopStack)
    
    // variablesAfterを持つループがあれば、履歴内の対応するノードを更新
    if (loopStack.length > 0) {
      const currentOuterIteration = loopStack.find(l => l.level === 0)?.iteration
      
      loopStack.forEach(activeLoop => {
        if (activeLoop.variablesAfter && activeLoop.variablesAfter.length > 0) {
          // 対応するノードを探す
          const targetNode = this.executionHistory.find(node => 
            node.level === activeLoop.level && 
            node.type === activeLoop.type &&
            node.iteration === activeLoop.iteration &&
            (activeLoop.level === 0 || node.parentIteration === currentOuterIteration)
          )
          
          if (targetNode && (!targetNode.variablesAfter || targetNode.variablesAfter.length === 0)) {
            targetNode.variablesAfter = [...activeLoop.variablesAfter]
            
            // 次のイテレーションのノードの表示変数も更新
            const nextNode = this.executionHistory.find(node => 
              node.level === activeLoop.level && 
              node.type === activeLoop.type &&
              node.iteration === activeLoop.iteration + 1 &&
              (activeLoop.level === 0 || node.parentIteration === currentOuterIteration)
            )
            
            if (nextNode && (!nextNode.variables || nextNode.variables.length === 0)) {
              nextNode.variables = [...activeLoop.variablesAfter]
            }
          }
        }
      })
    }
    
    // 実行履歴がある場合は常に表示
    if (this.executionHistory.length > 0) {
      // ノードの位置を計算して更新
      const positionedNodes = this.calculateNodePositions(this.executionHistory)
      this.animateNodeTransition(positionedNodes)
    }
  }
  
  /**
   * 実行履歴を更新
   */
  private updateExecutionHistory(loopStack: LoopContext[]): void {
    // 現在のループスタックが空の場合
    if (loopStack.length === 0) {
      // 実行完了後も履歴を保持（clearHistoryが明示的に呼ばれるまで）
      // 実行完了後も履歴を保持（clearHistoryが明示的に呼ばれるまで）
      this.lastLoopStack = ''
      return
    }
    
    // 現在のループスタックの状態を文字列化
    const currentStackState = this.serializeLoopStack(loopStack)
    // 現在と前回のループスタックを比較
    
    // デバッグ：ループスタックの状態を出力
    // console.log('[LoopFlowVisualizer] Loop stack state:', currentStackState)
    // console.log('[LoopFlowVisualizer] Loop stack details:', loopStack.map(l => ({
    //   type: l.type,
    //   level: l.level,
    //   iteration: l.iteration,
    //   condition: l.condition,
    //   conditionResult: l.conditionResult
    // })))
    
    // 前回と同じ状態の場合はスキップ
    if (currentStackState === this.lastLoopStack) {
      // 状態変化なし、スキップ
      // console.log('[LoopFlowVisualizer] State unchanged, skipping')
      return
    }
    
    // 現在の外側ループのイテレーション番号を取得（ネストしたループ用）
    let currentOuterIteration: number | undefined
    if (loopStack.length > 1) {
      // 外側のループ（level=0）を探す
      const outerLoop = loopStack.find(l => l.level === 0)
      if (outerLoop) {
        currentOuterIteration = outerLoop.iteration
        // console.log('[LoopFlowVisualizer] Current outer iteration:', currentOuterIteration)
      }
    }
    
    // スタック内のすべてのループを処理（最上位だけでなく）
    for (const activeLoop of loopStack) {
      // 内側のループの場合、親のイテレーションと組み合わせて一意性を判定
      const loopIdentifier = activeLoop.level > 0 && currentOuterIteration !== undefined
        ? `${activeLoop.level}-${activeLoop.type}-${activeLoop.iteration}-${currentOuterIteration}`
        : `${activeLoop.level}-${activeLoop.type}-${activeLoop.iteration}`
      
      // この組み合わせのノードが既に存在するかチェック
      const existsInHistory = this.executionHistory.some(node => {
        if (node.level !== activeLoop.level || node.type !== activeLoop.type || node.iteration !== activeLoop.iteration) {
          return false
        }
        // 内側のループの場合、親のイテレーションも一致する必要がある
        if (node.level > 0) {
          return node.parentIteration === currentOuterIteration
        }
        return true
      })
      
      if (!existsInHistory) {
        // console.log('[LoopFlowVisualizer] New node will be added:', {
        //   type: activeLoop.type,
        //   level: activeLoop.level,
        //   iteration: activeLoop.iteration,
        //   parentIteration: currentOuterIteration,
        //   condition: activeLoop.condition,
        //   conditionResult: activeLoop.conditionResult,
        //   isOuterLoop: activeLoop.level === 0,
        //   currentHistoryLength: this.executionHistory.length
        // })
        
        // 前のイテレーションのvariablesAfterを探す
        let displayVariables = activeLoop.variables ? [...activeLoop.variables] : undefined
        
        // 同じループの前のイテレーションを探す
        const previousNode = this.executionHistory.filter(n => 
          n.level === activeLoop.level && 
          n.type === activeLoop.type &&
          n.iteration === activeLoop.iteration - 1 &&
          (activeLoop.level === 0 || n.parentIteration === currentOuterIteration)
        ).pop()
        
        // 前のイテレーションのvariablesAfterが存在する場合はそれを使用
        if (previousNode && previousNode.variablesAfter) {
          displayVariables = [...previousNode.variablesAfter]
        }
        
        // ループの条件評価時に新しいノードを追加
        const snapshot: LoopNode = {
          id: this.nodeIdCounter++,
          type: activeLoop.type,
          level: activeLoop.level,
          x: 0,
          y: 0,
          width: 120 * this.scale,  // do-whileに合わせて120pxに設定
          height: 85 * this.scale,
          iteration: activeLoop.iteration,
          condition: activeLoop.condition,
          evaluatedCondition: activeLoop.evaluatedCondition || activeLoop.condition,
          conditionResult: activeLoop.conditionResult,
          parentIteration: currentOuterIteration,
          variables: displayVariables,  // 前のイテレーションの終了時の値を表示
          variablesAfter: activeLoop.variablesAfter ? [...activeLoop.variablesAfter] : undefined,
          isActive: false,  // 履歴ノードは非アクティブ
          opacity: 1,
          scale: 1,
          glowIntensity: 0,
        }
        
        // 履歴に追加
        this.executionHistory.push(snapshot)
        // console.log('[LoopFlowVisualizer] Added to history. Total nodes:', this.executionHistory.length)
        // console.log('[LoopFlowVisualizer] Nodes with parentIteration:', 
        //   this.executionHistory.filter(n => n.parentIteration !== undefined).length
        // )
      } else {
        // console.log('[LoopFlowVisualizer] Node already exists, skipping:', {
        //   type: activeLoop.type,
        //   level: activeLoop.level,
        //   iteration: activeLoop.iteration,
        //   parentIteration: currentOuterIteration,
        //   existingNodes: this.executionHistory.filter(n => 
        //     n.level === activeLoop.level && 
        //     n.type === activeLoop.type && 
        //     n.iteration === activeLoop.iteration
        //   ).map(n => ({
        //     id: n.id,
        //     iteration: n.iteration,
        //     parentIteration: n.parentIteration
        //   }))
        // })
      }
    }
    
    // console.log('[LoopFlowVisualizer] History nodes by level:', 
    //   this.executionHistory.reduce((acc, node) => {
    //     acc[node.level] = (acc[node.level] || 0) + 1
    //     return acc
    //   }, {} as Record<number, number>)
    // )
    
    // 注：variablesAfterの更新はupdateLoopStackメソッドに移動
    
    // 最後のノードをアクティブにする
    if (this.executionHistory.length > 0) {
      // 以前のアクティブノードを非アクティブに
      this.executionHistory.forEach(node => node.isActive = false)
      // 最後のノードをアクティブに
      this.executionHistory[this.executionHistory.length - 1].isActive = true
    }
    
    // 現在の状態を保存
    this.lastLoopStack = currentStackState
  }
  
  /**
   * ループスタックを文字列化（状態の変化を検出するため）
   */
  private serializeLoopStack(loopStack: LoopContext[]): string {
    return loopStack.map(loop => 
      `${loop.level}-${loop.type}-${loop.iteration}-${loop.conditionResult}`
    ).join('|')
  }
  
  /**
   * ノードの高さを計算
   */
  private calculateNodeHeight(node: LoopNode): number {
    const baseHeight = 85 * this.scale
    const variableLineHeight = 15 * this.scale
    const variableCount = node.variables?.length || 0
    
    // 基本の高さ + 変数表示エリアの高さ
    if (variableCount > 0) {
      // 変数が3つ以上の場合は最大3行まで表示
      const displayLines = Math.min(variableCount, 3)
      return baseHeight + (displayLines * variableLineHeight) + 10 * this.scale
    }
    
    return baseHeight
  }

  /**
   * ノードの位置を計算
   */
  private calculateNodePositions(nodes: LoopNode[]): LoopNode[] {
    const positionedNodes: LoopNode[] = []
    
    // デバッグ：入力ノードの情報を出力
    // console.log('[LoopFlowVisualizer] calculateNodePositions called with', nodes.length, 'nodes')
    // console.log('[LoopFlowVisualizer] Nodes by level:', nodes.map(n => ({
    //   level: n.level,
    //   iteration: n.iteration,
    //   condition: n.condition,
    //   type: n.type
    // })))
    
    // 各ループの位置とサイズを計算
    const nodeWidth = 120 * this.scale  // do-whileに合わせて120pxに設定
    const baseNodeHeight = 85 * this.scale
    const nodeGap = 25 * this.scale  // ノード間の固定ギャップ
    const horizontalGap = 140 * this.scale  // ノード幅に合わせて140pxに設定
    const innerLoopOffset = 25 * this.scale  // 25pxに設定
    const padding = 30
    
    // 必要なキャンバスサイズを計算
    let maxX = 0
    let maxY = 0
    
    // 1重ループの場合は縦に配置
    const hasNestedLoop = nodes.some(n => n.level === 1)
    // console.log('[LoopFlowVisualizer] Has nested loop:', hasNestedLoop)
    
    if (!hasNestedLoop) {
      // 1重ループ：縦に配置
      let currentY = padding
      nodes.forEach((node) => {
        const nodeHeight = this.calculateNodeHeight(node)
        const positionedNode: LoopNode = {
          ...node,
          x: padding,
          y: currentY,
          width: nodeWidth,
          height: nodeHeight,
        }
        positionedNodes.push(positionedNode)
        currentY += nodeHeight + nodeGap  // シンプルに高さ+ギャップ
        maxX = Math.max(maxX, padding + nodeWidth)
        maxY = Math.max(maxY, positionedNode.y + nodeHeight)
      })
    } else {
      // 2重ループ：外側は縦、内側は横に配置
      const outerLoopPositions: Map<number, number> = new Map() // iteration -> Y position
      let currentOuterY = padding
      let currentOuterIteration = -1
      let innerLoopCountInCurrentOuter = 0
      
      // 各外側ループのイテレーションごとの内側ループカウントを記録
      const innerLoopCounts: Map<number, number> = new Map()
      
      // まず外側ループ（level=0）のY位置を全て計算
      let accumulatedY = padding
      nodes.forEach((node) => {
        if (node.level === 0) {
          // console.log('[LoopFlowVisualizer] Checking outer loop node for Y position:', {
          //   iteration: node.iteration,
          //   conditionResult: node.conditionResult,
          //   shouldInclude: node.iteration > 0
          // })
          if (node.iteration > 0) {
            // 前のノードがある場合は、その高さを考慮してY位置を計算
            const nodeHeight = this.calculateNodeHeight(node)
            if (node.iteration === 1) {
              accumulatedY = padding  // 最初のノードはpaddingから開始
            } else {
              // 前のノードの高さを取得（前のイテレーションのノードを探す）
              const prevNode = nodes.find(n => n.level === 0 && n.iteration === node.iteration - 1)
              if (prevNode) {
                const prevHeight = this.calculateNodeHeight(prevNode)
                const prevY = outerLoopPositions.get(node.iteration - 1) || padding
                accumulatedY = prevY + prevHeight + nodeGap
              }
            }
            outerLoopPositions.set(node.iteration, accumulatedY)
            // console.log('[LoopFlowVisualizer] Pre-calculated Y position for outer loop:', {
            //   iteration: node.iteration,
            //   y: accumulatedY,
            //   nodeHeight: nodeHeight
            // })
          }
        }
      })
      
      // 内側ループが属する外側ループのイテレーションを特定するためのマップ
      const innerToOuterMap: Map<number, number> = new Map()
      let lastOuterIteration = 0
      
      // 実行履歴順に処理して、内側ループと外側ループの関係を構築
      nodes.forEach((node, index) => {
        if (node.level === 0) {
          lastOuterIteration = node.iteration
          // console.log('[LoopFlowVisualizer] Found outer loop iteration:', lastOuterIteration)
        } else if (node.level === 1) {
          // parentIterationがある場合はそれを使用、ない場合は実行順序から推測
          const mappedIteration = node.parentIteration !== undefined ? node.parentIteration : lastOuterIteration
          innerToOuterMap.set(index, mappedIteration)
          // console.log('[LoopFlowVisualizer] Found inner loop, mapping to outer iteration:', mappedIteration, 'node:', {
          //   id: node.id,
          //   iteration: node.iteration,
          //   parentIteration: node.parentIteration
          // })
        }
      })
      
      // 実行履歴順に処理（時系列順）
      nodes.forEach((node, index) => {
        if (node.level === 0) {
          // console.log('[LoopFlowVisualizer] Processing outer loop node:', {
          //   index: index,
          //   iteration: node.iteration,
          //   conditionResult: node.conditionResult,
          //   currentOuterIteration: currentOuterIteration
          // })
          
          // 外側のループ
          // イテレーション0（初期化時）はスキップして、実際のループのみ表示
          if (node.iteration === 0) {
            // console.log('[LoopFlowVisualizer] Skipping iteration 0')
            // イテレーション0は表示しないが、currentOuterIterationは設定
            currentOuterIteration = 0
            if (!node.conditionResult) {
              // falseで終了する場合のみ表示
              const nodeHeightZero = this.calculateNodeHeight(node)
              const positionedNode: LoopNode = {
                ...node,
                x: padding,
                y: currentOuterY,
                width: nodeWidth,
                height: nodeHeightZero,
              }
              positionedNodes.push(positionedNode)
              currentOuterY += nodeHeightZero + nodeGap
              maxX = Math.max(maxX, padding + nodeWidth)
              maxY = Math.max(maxY, positionedNode.y + nodeHeightZero)
            }
            return
          }
          
          // Y位置は事前に計算済みなので取得
          const y = outerLoopPositions.get(node.iteration) || currentOuterY
          
          // console.log('[LoopFlowVisualizer] Positioning outer loop node:', {
          //   iteration: node.iteration,
          //   y: y,
          //   outerLoopPositions: Array.from(outerLoopPositions.entries()),
          //   currentOuterY: currentOuterY
          // })
          
          const nodeHeight = this.calculateNodeHeight(node)
          const positionedNode: LoopNode = {
            ...node,
            x: padding,
            y: y,
            width: nodeWidth,
            height: nodeHeight,
          }
          positionedNodes.push(positionedNode)
          
          currentOuterIteration = node.iteration
          currentOuterY = y + nodeHeight + nodeGap  // 実際の高さ+ギャップを使用
          maxX = Math.max(maxX, padding + nodeWidth)
          maxY = Math.max(maxY, positionedNode.y + nodeHeight)
          
        } else if (node.level === 1) {
          // 内側のループ
          // この内側ループが属する外側ループのイテレーションを取得
          const belongsToOuterIteration = innerToOuterMap.get(index) || currentOuterIteration
          
          // 対応する外側ループのY位置を取得（事前に計算済み）
          const outerY = outerLoopPositions.get(belongsToOuterIteration)
          
          if (outerY !== undefined) {
            // このイテレーションの内側ループカウントを取得
            const currentInnerCount = innerLoopCounts.get(belongsToOuterIteration) || 0
            
            const x = padding + nodeWidth + innerLoopOffset + (currentInnerCount * horizontalGap)
            const y = outerY
            
            const nodeHeight = this.calculateNodeHeight(node)
            const positionedNode: LoopNode = {
              ...node,
              x,
              y,
              width: nodeWidth,
              height: nodeHeight,
            }
            positionedNodes.push(positionedNode)
            
            // 内側ループカウントを更新
            innerLoopCounts.set(belongsToOuterIteration, currentInnerCount + 1)
            
            maxX = Math.max(maxX, x + nodeWidth)
            maxY = Math.max(maxY, y + nodeHeight)
          }
        }
      })
    }
    
    // キャンバスサイズを必要に応じて更新
    const newWidth = Math.max(maxX + padding, 800)
    const newHeight = Math.max(maxY + padding, 600)
    
    if (newWidth !== this.canvasWidth || newHeight !== this.canvasHeight) {
      this.updateCanvasSize(newWidth, newHeight)
    }
    
    // console.log('[LoopFlowVisualizer] Final positioned nodes:', positionedNodes.map(n => ({
    //   level: n.level,
    //   iteration: n.iteration,
    //   x: n.x,
    //   y: n.y,
    //   type: n.type
    // })))
    
    return positionedNodes
  }
  
  /**
   * アニメーションを開始
   */
  private startAnimation(): void {
    const animate = () => {
      this.update()
      this.draw()
      this.animationFrame = requestAnimationFrame(animate)
    }
    animate()
  }
  
  /**
   * アニメーションの更新
   */
  private update(): void {
    const currentTime = Date.now()
    const deltaTime = currentTime - this.animationStartTime
    this.animationStartTime = currentTime
    
    // ノードのアニメーション
    this.loopNodes.forEach(node => {
      // 位置のアニメーション
      if (node.targetX !== undefined) {
        node.x += (node.targetX - node.x) * 0.1
        if (Math.abs(node.targetX - node.x) < 0.5) {
          node.x = node.targetX
          node.targetX = undefined
        }
      }
      
      if (node.targetY !== undefined) {
        node.y += (node.targetY - node.y) * 0.1
        if (Math.abs(node.targetY - node.y) < 0.5) {
          node.y = node.targetY
          node.targetY = undefined
        }
      }
      
      // オパシティのアニメーション
      const targetOpacity = 1
      node.opacity += (targetOpacity - node.opacity) * 0.15
      
      // スケールのアニメーション
      const targetScale = node.isActive ? 1.05 : 1
      node.scale += (targetScale - node.scale) * 0.1
      
      // グロー効果のアニメーション
      if (node.isActive) {
        node.glowIntensity = 0.5 + Math.sin(currentTime * 0.003) * 0.3
      } else {
        node.glowIntensity *= 0.95
      }
    })
    
    // フローパーティクルの更新
    this.updateFlowParticles()
  }
  
  /**
   * ノードの遷移をアニメーション
   */
  private animateNodeTransition(newNodes: LoopNode[]): void {
    // 既存のノードと新しいノードをマッチング
    newNodes.forEach((newNode, index) => {
      const existingNode = this.loopNodes[index]
      if (existingNode) {
        // 既存のノードを更新
        newNode.x = existingNode.x
        newNode.y = existingNode.y
        newNode.opacity = existingNode.opacity
        newNode.scale = existingNode.scale
        newNode.glowIntensity = existingNode.glowIntensity
        
        // 条件が変わったらパーティクルを生成
        if (existingNode.conditionResult !== newNode.conditionResult) {
          this.createConditionChangeEffect(newNode)
        }
      } else {
        // 新しいノードはフェードインで登場
        newNode.opacity = 0
        newNode.scale = 0.8
      }
    })
    
    this.loopNodes = newNodes
  }
  
  /**
   * 条件変更エフェクトを作成
   */
  private createConditionChangeEffect(node: LoopNode): void {
    const particleCount = 20
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount
      const speed = 2 + Math.random() * 2
      this.flowParticles.push({
        x: node.x + node.width / 2,
        y: node.y + node.height / 2,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        color: node.conditionResult ? this.options.secondaryColor : this.options.errorColor,
        size: 3 + Math.random() * 3,
      })
    }
  }
  
  /**
   * フローパーティクルを更新
   */
  private updateFlowParticles(): void {
    this.flowParticles = this.flowParticles.filter(particle => {
      particle.x += particle.vx
      particle.y += particle.vy
      particle.vx *= 0.98
      particle.vy *= 0.98
      particle.life *= 0.95
      particle.size *= 0.98
      return particle.life > 0.01
    })
  }
  
  /**
   * 描画
   */
  private draw(): void {
    // 背景をクリア（実際のキャンバスサイズで）
    this.ctx.fillStyle = this.options.backgroundColor
    this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)
    
    if (this.loopNodes.length === 0) {
      this.drawEmptyState()
      return
    }
    
    // 接続線を描画
    this.drawConnections()
    
    // フローパーティクルを描画
    this.drawFlowParticles()
    
    // ループノードを描画
    this.loopNodes.forEach(node => {
      if (node.opacity > 0.01) {
        this.drawLoopNode(node)
      }
    })
    
    // フロー矢印を描画
    this.drawFlowArrows()
  }
  
  /**
   * 空状態を描画
   */
  private drawEmptyState(): void {
    const centerX = this.canvasWidth / 2
    const centerY = this.canvasHeight / 2
    
    this.ctx.fillStyle = '#6b7280'
    this.ctx.font = '14px sans-serif'
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText('ループフロー可視化', centerX, centerY - 20)
    
    this.ctx.font = '12px sans-serif'
    this.ctx.fillStyle = '#9ca3af'
    this.ctx.fillText('実行時にループの流れが表示されます', centerX, centerY + 10)
  }
  
  /**
   * ループノードを描画
   */
  private drawLoopNode(node: LoopNode): void {
    const { x, y, width, height } = node
    
    this.ctx.save()
    
    // オパシティとスケールを適用
    this.ctx.globalAlpha = node.opacity
    this.ctx.translate(x + width / 2, y + height / 2)
    this.ctx.scale(node.scale, node.scale)
    this.ctx.translate(-(x + width / 2), -(y + height / 2))
    
    // グロー効果を描画
    if (node.isActive && node.glowIntensity > 0) {
      this.ctx.shadowColor = this.options.primaryColor
      this.ctx.shadowBlur = 20 * node.glowIntensity
      this.roundRect(x, y, width, height, 8)
      this.ctx.strokeStyle = this.options.primaryColor
      this.ctx.lineWidth = 3
      this.ctx.stroke()
      this.ctx.shadowBlur = 0
    }
    
    // 影を描画
    if (node.isActive) {
      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.1)'
      this.ctx.shadowBlur = 10
      this.ctx.shadowOffsetX = 0
      this.ctx.shadowOffsetY = 4
    }
    
    // 背景を描画
    this.ctx.fillStyle = node.isActive ? '#ffffff' : '#f9fafb'
    this.ctx.strokeStyle = node.isActive 
      ? this.options.primaryColor 
      : node.conditionResult 
        ? this.options.secondaryColor 
        : this.options.errorColor
    this.ctx.lineWidth = node.isActive ? 3 : 2
    
    this.roundRect(x, y, width, height, 8)
    this.ctx.fill()
    this.ctx.stroke()
    
    // 影をリセット
    this.ctx.shadowColor = 'transparent'
    this.ctx.shadowBlur = 0
    this.ctx.shadowOffsetX = 0
    this.ctx.shadowOffsetY = 0
    
    // ヘッダーを描画
    this.ctx.fillStyle = node.isActive ? this.options.primaryColor : '#f3f4f6'
    this.roundRect(x, y, width, 30, 8, true, false)
    this.ctx.fill()
    
    // ループタイプを描画
    this.ctx.fillStyle = node.isActive ? '#ffffff' : this.options.primaryColor
    this.ctx.font = 'bold 11px sans-serif'
    this.ctx.textAlign = 'left'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText(node.type.toUpperCase(), x + 8, y + 15)
    
    // ネストレベルを表示
    if (node.level > 0) {
      this.ctx.fillStyle = node.isActive ? '#ffffff' : '#6b7280'
      this.ctx.font = '9px sans-serif'
      this.ctx.textAlign = 'center'
      this.ctx.fillText(`L${node.level}`, x + width / 2, y + 15)
    }
    
    // イテレーション回数を描画
    this.ctx.fillStyle = node.isActive ? '#ffffff' : '#6b7280'
    this.ctx.font = '10px sans-serif'
    this.ctx.textAlign = 'right'
    this.ctx.fillText(`iter: ${node.iteration}`, x + width - 8, y + 15)
    
    // 条件式を描画（元の式）
    this.ctx.fillStyle = '#6b7280'
    this.ctx.font = '10px monospace'
    this.ctx.textAlign = 'left'
    this.ctx.textBaseline = 'middle'
    const conditionText = this.truncateText(node.condition, width - 20)
    this.ctx.fillText(conditionText, x + 10, y + 40)
    
    // 評価済み条件式を描画（実際の値）
    this.ctx.fillStyle = '#1e40af'
    this.ctx.font = 'bold 11px monospace'
    const evaluatedText = this.truncateText(node.evaluatedCondition, width - 20)
    this.ctx.fillText(evaluatedText, x + 10, y + 55)
    
    // 条件結果を描画
    const resultColor = node.conditionResult ? this.options.secondaryColor : this.options.errorColor
    const resultText = node.conditionResult ? 'true ✓' : 'false ✗'
    
    this.ctx.fillStyle = resultColor
    this.ctx.font = 'bold 12px sans-serif'
    this.ctx.textAlign = 'center'
    this.ctx.fillText(resultText, x + width / 2, y + 72)
    
    // フロー方向を示すアイコン
    if (node.conditionResult) {
      this.drawContinueIcon(x + width - 20, y + height - 20)
    } else {
      this.drawExitIcon(x + width - 20, y + height - 20)
    }
    
    // 変数値を描画（variablesAfterがあればそれを使用、なければvariablesを使用）
    const displayVariables = node.variablesAfter && node.variablesAfter.length > 0 
      ? node.variablesAfter 
      : node.variables
    
    if (displayVariables && displayVariables.length > 0) {
      this.drawVariables(x, y + 85 * this.scale, width, displayVariables)
    }
    
    this.ctx.restore()
  }
  
  /**
   * 変数値を描画
   */
  private drawVariables(x: number, y: number, width: number, variables: Variable[]): void {
    const lineHeight = 15 * this.scale
    const padding = 10 * this.scale
    const fontSize = 10 * this.scale
    
    // 背景を描画（薄い色）
    this.ctx.fillStyle = '#f0f9ff'
    this.ctx.strokeStyle = '#dbeafe'
    this.ctx.lineWidth = 1
    
    const variableAreaHeight = Math.min(variables.length, 3) * lineHeight + padding
    this.roundRect(x + 5, y, width - 10, variableAreaHeight, 4)
    this.ctx.fill()
    this.ctx.stroke()
    
    // 変数を描画（最大3つまで表示）
    this.ctx.font = `${fontSize}px monospace`
    this.ctx.textAlign = 'left'
    this.ctx.textBaseline = 'middle'
    
    const displayVariables = variables.slice(0, 3)
    displayVariables.forEach((variable, index) => {
      const varY = y + padding / 2 + (index + 0.5) * lineHeight
      
      // 変更された変数は色を変える
      if (variable.changed) {
        this.ctx.fillStyle = '#dc2626'  // 赤色で強調
        this.ctx.font = `bold ${fontSize}px monospace`
      } else {
        this.ctx.fillStyle = '#374151'  // 通常の色
        this.ctx.font = `${fontSize}px monospace`
      }
      
      // 変数名と値を表示
      const varText = `${variable.name}: ${variable.value}`
      const truncatedText = this.truncateText(varText, width - padding * 2)
      this.ctx.fillText(truncatedText, x + padding, varY)
    })
    
    // 3つ以上の変数がある場合は「...」を表示
    if (variables.length > 3) {
      this.ctx.fillStyle = '#6b7280'
      this.ctx.font = `${fontSize}px sans-serif`
      this.ctx.textAlign = 'center'
      this.ctx.fillText('...', x + width / 2, y + variableAreaHeight - padding / 2)
    }
  }

  /**
   * 接続線を描画
   */
  private drawConnections(): void {
    // ノード間の関係を分析して接続を描画
    for (let i = 0; i < this.loopNodes.length - 1; i++) {
      const current = this.loopNodes[i]
      const next = this.loopNodes[i + 1]
      
      // 透明なノードはスキップ
      if (current.opacity < 0.1 || next.opacity < 0.1) continue
      
      // レベル差を計算
      const levelDiff = next.level - current.level
      
      if (levelDiff > 0) {
        // 親から子への接続（レベルが増加）
        this.drawParentToChildConnection(current, next)
      } else if (current.level === 1 && next.level === 1) {
        // 内側ループ間の接続（横方向）
        // 同じ行にある場合のみ横接続
        if (Math.abs(current.y - next.y) < 10) {
          this.drawInnerLoopConnection(current, next)
        }
      } else if (levelDiff < 0) {
        // 子から親への接続（レベルが減少）
        this.drawChildToParentConnection(current, next)
      } else if (current.level === 0 && next.level === 0) {
        // 外側ループ間の接続（縦方向）
        this.drawSameLevelConnection(current, next)
      }
    }
  }
  
  /**
   * 親から子への接続線を描画
   */
  private drawParentToChildConnection(parent: LoopNode, child: LoopNode): void {
    // レベル0からレベル1への接続のみ（レベル2以上はサポートしない）
    if (parent.level !== 0 || child.level !== 1) {
      return
    }
    
    this.ctx.strokeStyle = '#3b82f6'
    this.ctx.lineWidth = 3
    this.ctx.setLineDash([])
    
    this.ctx.beginPath()
    
    // 外側から内側への接続（横方向）
    const startX = parent.x + parent.width
    const startY = parent.y + parent.height / 2
    const endX = child.x
    const endY = child.y + child.height / 2
    
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(startX + 20, startY)
    this.ctx.lineTo(startX + 20, endY)
    this.ctx.lineTo(endX, endY)
    
    // 矢印を描画
    this.drawArrowHead(endX, endY, 0)
    
    this.ctx.stroke()
  }
  
  /**
   * 内側ループ間の接続線を描画
   */
  private drawInnerLoopConnection(current: LoopNode, next: LoopNode): void {
    this.ctx.strokeStyle = '#e5e7eb'
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([5, 5])
    
    this.ctx.beginPath()
    
    // 横方向の接続
    const startX = current.x + current.width
    const startY = current.y + current.height / 2
    const endX = next.x
    const endY = next.y + next.height / 2
    
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(endX, endY)
    
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }
  
  /**
   * 子から親への接続線を描画
   */
  private drawChildToParentConnection(child: LoopNode, parent: LoopNode): void {
    // レベル1からレベル0への接続のみ（レベル2以上はサポートしない）
    if (child.level !== 1 || parent.level !== 0) {
      return
    }
    
    this.ctx.strokeStyle = '#9ca3af'
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([3, 3])
    
    this.ctx.beginPath()
    
    // 内側から外側への接続
    const startX = child.x + child.width / 2
    const startY = child.y + child.height
    const endX = parent.x + parent.width / 2
    const endY = parent.y
    
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(startX, startY + 20)
    this.ctx.lineTo(endX, startY + 20)
    this.ctx.lineTo(endX, endY)
    
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }
  
  /**
   * 同レベルの接続線を描画
   */
  private drawSameLevelConnection(current: LoopNode, next: LoopNode): void {
    this.ctx.strokeStyle = '#e5e7eb'
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([5, 5])
    
    this.ctx.beginPath()
    
    // 縦方向の接続
    const startX = current.x + current.width / 2
    const startY = current.y + current.height
    const endX = next.x + next.width / 2
    const endY = next.y
    
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(endX, endY)
    
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }
  
  /**
   * フロー矢印を描画
   */
  private drawFlowArrows(): void {
    // ループの流れを示す矢印を描画
    this.loopNodes.forEach((node, index) => {
      if (node.conditionResult && index === this.loopNodes.length - 1) {
        // ループバック矢印
        this.drawLoopBackArrow(node)
      }
    })
  }
  
  /**
   * ループバック矢印を描画
   */
  private drawLoopBackArrow(node: LoopNode): void {
    // レベル2以上はサポートしない
    if (node.level >= 2) {
      return
    }
    
    if (node.level === 0) {
      // 外側ループのループバック（左側）
      const margin = 30 * this.scale
      const startX = node.x - margin
      const startY = node.y + node.height - 20
      const endX = node.x - margin
      const endY = node.y + 20
      
      this.ctx.strokeStyle = this.options.primaryColor
      this.ctx.lineWidth = 2
      this.ctx.setLineDash([])
      
      this.ctx.beginPath()
      this.ctx.moveTo(startX, startY)
      // 左に曲がってから上に戻る
      this.ctx.bezierCurveTo(
        startX - 20, startY,
        startX - 20, endY,
        endX, endY
      )
      this.ctx.stroke()
      
      // 矢印の先端
      this.drawArrowHead(endX, endY, -Math.PI / 2)
    } else if (node.level === 1) {
      // 内側ループのループバック（下側）
      const margin = 20 * this.scale
      const startX = node.x + node.width - 20
      const startY = node.y + node.height + margin
      const endX = node.x + 20
      const endY = node.y + node.height + margin
      
      this.ctx.strokeStyle = this.options.primaryColor
      this.ctx.lineWidth = 2
      this.ctx.setLineDash([])
      
      this.ctx.beginPath()
      this.ctx.moveTo(startX, startY)
      // 下を通って左に戻る
      this.ctx.bezierCurveTo(
        startX, startY + 10,
        endX, startY + 10,
        endX, endY
      )
      this.ctx.stroke()
      
      // 矢印の先端
      this.drawArrowHead(endX, endY, Math.PI)
    }
  }
  
  /**
   * 矢印の先端を描画
   */
  private drawArrowHead(x: number, y: number, angle: number): void {
    const headLength = 10
    
    this.ctx.save()
    this.ctx.translate(x, y)
    this.ctx.rotate(angle)
    
    this.ctx.beginPath()
    this.ctx.moveTo(0, 0)
    this.ctx.lineTo(-headLength / 2, -headLength)
    this.ctx.lineTo(headLength / 2, -headLength)
    this.ctx.closePath()
    this.ctx.fillStyle = this.options.primaryColor
    this.ctx.fill()
    
    this.ctx.restore()
  }
  
  /**
   * 継続アイコンを描画
   */
  private drawContinueIcon(x: number, y: number): void {
    this.ctx.strokeStyle = this.options.secondaryColor
    this.ctx.lineWidth = 1.5
    
    // 円形矢印
    this.ctx.beginPath()
    this.ctx.arc(x, y, 6, -Math.PI / 2, Math.PI)
    this.ctx.stroke()
    
    // 矢印の先端
    this.ctx.beginPath()
    this.ctx.moveTo(x - 6, y)
    this.ctx.lineTo(x - 9, y - 3)
    this.ctx.lineTo(x - 9, y + 3)
    this.ctx.closePath()
    this.ctx.fillStyle = this.options.secondaryColor
    this.ctx.fill()
  }
  
  /**
   * 終了アイコンを描画
   */
  private drawExitIcon(x: number, y: number): void {
    this.ctx.strokeStyle = this.options.errorColor
    this.ctx.lineWidth = 1.5
    
    // 右向き矢印
    this.ctx.beginPath()
    this.ctx.moveTo(x - 8, y)
    this.ctx.lineTo(x + 4, y)
    this.ctx.stroke()
    
    // 矢印の先端
    this.ctx.beginPath()
    this.ctx.moveTo(x + 4, y)
    this.ctx.lineTo(x, y - 3)
    this.ctx.lineTo(x, y + 3)
    this.ctx.closePath()
    this.ctx.fillStyle = this.options.errorColor
    this.ctx.fill()
  }
  
  /**
   * 角丸矩形を描画
   */
  private roundRect(x: number, y: number, width: number, height: number, radius: number, topOnly = false, bottomOnly = false): void {
    this.ctx.beginPath()
    
    if (topOnly) {
      this.ctx.moveTo(x + radius, y)
      this.ctx.lineTo(x + width - radius, y)
      this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
      this.ctx.lineTo(x + width, y + height)
      this.ctx.lineTo(x, y + height)
      this.ctx.lineTo(x, y + radius)
      this.ctx.quadraticCurveTo(x, y, x + radius, y)
    } else if (bottomOnly) {
      this.ctx.moveTo(x, y)
      this.ctx.lineTo(x + width, y)
      this.ctx.lineTo(x + width, y + height - radius)
      this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
      this.ctx.lineTo(x + radius, y + height)
      this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
      this.ctx.lineTo(x, y)
    } else {
      this.ctx.moveTo(x + radius, y)
      this.ctx.lineTo(x + width - radius, y)
      this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
      this.ctx.lineTo(x + width, y + height - radius)
      this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
      this.ctx.lineTo(x + radius, y + height)
      this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
      this.ctx.lineTo(x, y + radius)
      this.ctx.quadraticCurveTo(x, y, x + radius, y)
    }
    
    this.ctx.closePath()
  }
  
  /**
   * テキストを切り詰め
   */
  private truncateText(text: string, maxWidth: number): string {
    const metrics = this.ctx.measureText(text)
    if (metrics.width <= maxWidth) {
      return text
    }
    
    let truncated = text
    while (this.ctx.measureText(truncated + '...').width > maxWidth && truncated.length > 0) {
      truncated = truncated.slice(0, -1)
    }
    
    return truncated + '...'
  }
  
  /**
   * フローパーティクルを描画
   */
  private drawFlowParticles(): void {
    this.flowParticles.forEach(particle => {
      this.ctx.save()
      this.ctx.globalAlpha = particle.life
      this.ctx.fillStyle = particle.color
      this.ctx.beginPath()
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
      this.ctx.fill()
      this.ctx.restore()
    })
  }
  
  /**
   * 履歴をクリア
   */
  clearHistory(): void {
    this.executionHistory = []
    this.lastLoopStack = ''
    this.loopNodes = []
    this.nodeIdCounter = 0
  }
  
  /**
   * クリーンアップ
   */
  dispose(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
    this.clearHistory()
  }
}

// フローパーティクルの型定義
interface FlowParticle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}